import * as React from 'react';
import { CONNECTIONS_PAGE } from 'constants/pages';
import { NotificationActionTypesEnum, NotificationStatusesEnum } from 'types/generated/client';
import Link from 'components/Link';
import NotificationActivityCard, {
  type NotificationPillTone,
  NotificationPlayerCard,
} from './NotificationActivityCard';
import { NotificationTypeSelectorProps } from '../types';

export type ConnectionActionType =
  | NotificationActionTypesEnum.ConnectionRequestReceived
  | NotificationActionTypesEnum.ConnectionRequestAccepted;

const VARIANTS: Record<
  ConnectionActionType,
  { pill: { label: string; tone: NotificationPillTone }; title: string }
> = {
  [NotificationActionTypesEnum.ConnectionRequestReceived]: {
    pill: { label: 'Connection Request', tone: 'received' },
    title: 'Wants to connect with you',
  },
  [NotificationActionTypesEnum.ConnectionRequestAccepted]: {
    pill: { label: 'Connected', tone: 'accepted' },
    title: 'Accepted your connection request',
  },
};

interface Props extends NotificationTypeSelectorProps {
  status: NotificationStatusesEnum;
  actionType: ConnectionActionType;
}

/**
 * A connection request received or accepted. Both land on the Connections page,
 * which is where the request is answered and where the new connection shows up.
 */
export default function ConnectionNotification({ notification, status, actionType }: Props) {
  const primaryEntity = notification?.notificationDetails?.primaryEntity;
  const request = primaryEntity?.connectionRequest;
  // The card shows whoever acted: the requester on a new request, the recipient on an accept.
  const actorProfile =
    actionType === NotificationActionTypesEnum.ConnectionRequestReceived
      ? request?.requesterProfile
      : request?.recipientProfile;

  return (
    <Link href={CONNECTIONS_PAGE} className="block">
      <NotificationActivityCard
        status={status}
        actorName={primaryEntity?.actingUserProfile?.fullName ?? 'A player'}
        actorCoachStatus={primaryEntity?.actingUserProfile?.coachStatus}
        message={request?.message}
        {...VARIANTS[actionType]}
      >
        {actorProfile && <NotificationPlayerCard profile={actorProfile} />}
      </NotificationActivityCard>
    </Link>
  );
}
